import {useEffect, useId, useLayoutEffect, useRef, useState, type ClipboardEvent, type Dispatch, type ReactNode, type SetStateAction} from 'react';
import type {ChatAttachment} from '../src/chat-attachments';
import {errorDetails} from './lib/store';
import {ErrorNotice} from './components';
import {Loading} from './main-loading';
import {useComposerSize} from './use-composer-size';
import './chat-composer.css';
import {platform} from './platform/transport';
import {UNSAVED_WORK_EVENT, type UnsavedWorkQuery} from './platform/unsaved-work';

export type ComposerAttachment = {id: string; file: File; uploaded?: ChatAttachment} | {id: string; file?: undefined; uploaded: ChatAttachment};
export type ComposerDraftReset = {previous: string; value: string};
export type ComposerEarlierDraft = {text: string; attachments: ComposerAttachment[]; pendingSubmission: boolean; append: () => void};
type RecognitionResult = {isFinal: boolean; 0: {transcript: string}};
type Recognition = {
  lang: string; continuous: boolean; interimResults: boolean;
  onresult: ((event: {resultIndex: number; results: ArrayLike<RecognitionResult>}) => void) | null;
  onerror: ((event: {error: string; message?: string}) => void) | null;
  onend: (() => void) | null;
  start(): void; stop(): void; abort(): void;
};
type RecognitionWindow = Window & {SpeechRecognition?: new () => Recognition; webkitSpeechRecognition?: new () => Recognition};

export function ChatComposer({agentName, draft, draftReset, earlierDraft, onDraftChange, working, disabled, attachments,
  onAttachmentsChange, uploadFile, onSend, onStop, errorContent}: {
  agentName: string; draft: string; onDraftChange: Dispatch<SetStateAction<string>>; working: boolean;
  draftReset?: ComposerDraftReset | undefined;
  earlierDraft?: ComposerEarlierDraft | undefined;
  disabled: boolean; attachments: ComposerAttachment[];
  onAttachmentsChange: Dispatch<SetStateAction<ComposerAttachment[]>>;
  uploadFile: (file: File) => Promise<ChatAttachment>;
  onSend: (text: string, attachments: ChatAttachment[]) => Promise<boolean>;
  onStop: () => Promise<void>; errorContent?: ReactNode;
}) {
  const textarea = useRef<HTMLTextAreaElement>(null);
  const picker = useRef<HTMLInputElement>(null);
  const recognition = useRef<Recognition | null>(null);
  const composing = useRef(false);
  const currentInterim = useRef('');
  const sendInFlight = useRef(false);
  const mounted = useRef(true);
  // The native textarea owns edits, selection, undo and IME composition. The
  // parent retains this draft by chat, without rendering the workspace per key.
  const initialDraft = useRef(draft);
  const currentDraft = useRef(draft);
  const appliedReset = useRef(draftReset);
  const setCurrentDraft = useRef(onDraftChange);
  setCurrentDraft.current = onDraftChange;
  const hasDraftRef = useRef(Boolean(draft.trim()));
  const [hasDraft, setHasDraft] = useState(hasDraftRef.current);
  const resizeDraft = useComposerSize(textarea);
  const [sending, setSending] = useState(false);
  const [listening, setListening] = useState(false);
  const [interim, setInterim] = useState('');
  const [error, setError] = useState<ReturnType<typeof errorDetails> | null>(null);
  const textareaId = useId();
  const attachmentHelpId = useId();
  const hasContent = hasDraft || Boolean(interim.trim() || attachments.length);
  const canSend = hasContent && !disabled && !sending;
  const controlsDisabled = disabled || sending;
  const dictationAvailable = platform().kind !== 'desktop' || Boolean((window as RecognitionWindow).SpeechRecognition || (window as RecognitionWindow).webkitSpeechRecognition);
  useEffect(() => {
    const inspect = (event: Event) => {
      if (currentDraft.current || currentInterim.current || sendInFlight.current || attachments.length > 0 || listening) {
        (event as UnsavedWorkQuery).detail.blocked = true;
      }
    };
    window.addEventListener(UNSAVED_WORK_EVENT, inspect);
    return () => window.removeEventListener(UNSAVED_WORK_EVENT, inspect);
  }, [attachments, listening]);
  function editDraft(value: string, persist = true) {
    currentDraft.current = value;
    if (persist) setCurrentDraft.current(value);
    if (!mounted.current) return;
    const input = textarea.current;
    // Ordinary keystrokes already changed the DOM. Never assign their value
    // back: only accepted sends and finalized speech change it programmatically.
    if (input && input.value !== value) input.value = value;
    const filled = Boolean(value.trim());
    if (hasDraftRef.current !== filled) {
      hasDraftRef.current = filled;
      setHasDraft(filled);
    }
    resizeDraft();
  }
  useLayoutEffect(() => {
    // An accepted send can finish after leaving this chat and reopening it.
    // Synchronize that explicit clear, but never overwrite a newer native edit.
    if (draftReset === appliedReset.current) return;
    appliedReset.current = draftReset;
    if (draftReset && currentDraft.current === draftReset.previous) editDraft(draftReset.value, false);
  }, [draftReset]);
  function abortDictation() {
    const engine = recognition.current;
    recognition.current = null;
    if (engine) {
      engine.onresult = null; engine.onerror = null; engine.onend = null;
      engine.abort();
    }
    currentInterim.current = '';
    if (mounted.current) {setListening(false); setInterim('');}
  }
  useEffect(() => {
    mounted.current = true;
    return () => {mounted.current = false; abortDictation();};
  }, []);
  useEffect(() => {
    if (!controlsDisabled) return;
    abortDictation();
  }, [controlsDisabled]);

  function chooseFiles() {
    if (sendInFlight.current) return;
    setError(null);
    // Selecting a local draft does not need a live chat connection. Keep the
    // picker in this click event so touch browsers retain user activation.
    try {
      if (!picker.current) throw new Error('The file picker is unavailable.');
      picker.current.click();
    } catch (failure) {setError(errorDetails(failure));}
  }

  function selectFiles(files: ArrayLike<File> | null) {
    if (!files) return;
    setError(null);
    const selected = Array.from(files);
    onAttachmentsChange(previous => [...previous, ...selected.map(file => ({id: crypto.randomUUID(), file}))]);
  }

  function pasteImages(event: ClipboardEvent<HTMLFormElement>) {
    const images = Array.from(event.clipboardData.files).filter(file => file.type.startsWith('image/'));
    if (!images.length) return;
    event.preventDefault();
    if (sendInFlight.current || sending) return;
    selectFiles(images);
  }

  async function send() {
    if (!canSend || composing.current || sendInFlight.current) return;
    sendInFlight.current = true;
    // A user can send while the browser still labels the visible words interim.
    // Keep exactly that displayed phrase in the submitted and retryable draft.
    const before = textarea.current?.value ?? currentDraft.current;
    const message = appendTranscript(before, currentInterim.current);
    editDraft(message);
    abortDictation();
    setError(null); setSending(true);
    const selection = attachments;
    try {
      const uploaded: ChatAttachment[] = [];
      for (const attachment of selection) {
        if (!mounted.current) return;
        const receipt = attachment.uploaded ?? await uploadFile(attachment.file!);
        uploaded.push(receipt);
        // Retain confirmed uploads on a rejected/uncertain message. A manual
        // resend can reuse them without overwriting or duplicating account files.
        if (!attachment.uploaded) onAttachmentsChange(previous => previous.map(item => item.id === attachment.id ? {...item, uploaded: receipt} : item));
      }
      if (!mounted.current) return;
      if (await onSend(message, uploaded)) {
        onAttachmentsChange(previous => previous.filter(item => !selection.some(sent => sent.id === item.id)));
        onDraftChange(current => current === message ? '' : current);
        if (mounted.current && currentDraft.current === message) editDraft('', false);
      }
    } catch (failure) {if (mounted.current) setError(errorDetails(failure));}
    finally {sendInFlight.current = false; if (mounted.current) setSending(false);}
  }

  function dictate() {
    if (controlsDisabled) return;
    if (listening) {recognition.current?.stop(); return;}
    setError(null);
    const browser = window as RecognitionWindow;
    const SpeechRecognition = browser.SpeechRecognition ?? browser.webkitSpeechRecognition;
    if (!SpeechRecognition || !window.isSecureContext) {
      setError(errorDetails(new Error('Voice dictation is unavailable in this browser. You can use the microphone on your phone’s keyboard instead.')));
      return;
    }
    const engine = new SpeechRecognition();
    recognition.current = engine;
    engine.lang = navigator.language;
    engine.continuous = true;
    engine.interimResults = true;
    engine.onresult = event => {
      if (!mounted.current || recognition.current !== engine) return;
      let finished = '', partial = '';
      for (let index = event.resultIndex; index < event.results.length; index++) {
        const result = event.results[index];
        if (!result) continue;
        if (result.isFinal) finished += result[0].transcript;
        else partial += result[0].transcript;
      }
      if (finished) {
        const before = currentDraft.current;
        const next = appendTranscript(before, finished);
        editDraft(next);
      }
      currentInterim.current = partial;
      setInterim(partial);
    };
    engine.onerror = event => {
      if (!mounted.current || recognition.current !== engine || event.error === 'aborted') return;
      setError(errorDetails(new Error(`Dictation failed (${event.error})${event.message ? `: ${event.message}` : '.'}`)));
    };
    engine.onend = () => {
      if (recognition.current !== engine) return;
      recognition.current = null;
      currentInterim.current = '';
      if (mounted.current) {setListening(false); setInterim('');}
    };
    try {engine.start(); setListening(true);}
    catch (failure) {recognition.current = null; setError(errorDetails(failure));}
  }

  return <form className="hermes-composer" data-unsaved-owner onSubmit={event => {event.preventDefault(); void send();}} onPaste={pasteImages}
    onDragOver={event => {if (event.dataTransfer.types.includes('Files')) event.preventDefault();}}
    onDrop={event => {
      if (!event.dataTransfer.files.length) return;
      event.preventDefault();
      if (!sendInFlight.current && !sending) selectFiles(event.dataTransfer.files);
    }} aria-label={`Message ${agentName}`}>
    <Loading active={sending}/>
    {earlierDraft && <section aria-label="Earlier draft">
      <p>Another draft was saved before this chat loaded. Both drafts are kept.</p>
      {earlierDraft.text && <p style={{whiteSpace: 'pre-wrap'}}>{earlierDraft.text}</p>}
      {earlierDraft.attachments.length > 0 && <ul>{earlierDraft.attachments.map(file =>
        <li key={file.id}>{file.file?.name ?? file.uploaded!.name}</li>)}</ul>}
      {earlierDraft.pendingSubmission && <p>Complete the pending send before you combine these drafts.</p>}
      <button type="button" className="text-button" disabled={sending || earlierDraft.pendingSubmission}
        onClick={() => {if (!sendInFlight.current && !earlierDraft.pendingSubmission) earlierDraft.append();}}>Add this draft to the current draft</button>
    </section>}
    {attachments.length > 0 && <ul className="composer-attachments" aria-label="Attached files">{attachments.map(attachment => <li key={attachment.id}>
      <span title={attachment.file?.name ?? attachment.uploaded!.name}>{attachment.file?.name ?? attachment.uploaded!.name}<small>{formatFileSize(attachment.file?.size ?? attachment.uploaded!.size)}</small></span>
      <button type="button" aria-label={`Remove ${attachment.file?.name ?? attachment.uploaded!.name} from this message`} disabled={sending}
        onClick={() => onAttachmentsChange(previous => previous.filter(item => item.id !== attachment.id))}><CloseIcon/></button>
    </li>)}</ul>}
    {listening && <div className="composer-dictation" role="status">{interim || 'Listening…'}</div>}
    <div className="composer-pill">
      <button type="button" className="composer-icon-button composer-attach" disabled={sending} onClick={chooseFiles}
        aria-label="Attach files" aria-describedby={attachmentHelpId} title="Attach files"><PlusIcon/></button>
      <span id={attachmentHelpId} className="visually-hidden">Attach a file or paste an image within your available account storage. Files upload when you send the message.</span>
      <input ref={picker} hidden disabled={sending} type="file" multiple tabIndex={-1} aria-label="Choose files to attach"
        onChange={event => {selectFiles(event.target.files); event.currentTarget.value = '';}}/>
      <label className="visually-hidden" htmlFor={textareaId}>Message {agentName}</label>
      <textarea ref={textarea} id={textareaId} defaultValue={initialDraft.current} rows={1} maxLength={32768} disabled={sending} onChange={event => editDraft(event.target.value)}
        onCompositionStart={() => {composing.current = true;}}
        onCompositionEnd={() => {composing.current = false;}}
        onKeyDown={event => {
          if (event.key === 'Enter' && !event.shiftKey && !composing.current && !event.nativeEvent.isComposing
            && event.nativeEvent.keyCode !== 229 && !window.matchMedia('(hover:none) and (pointer:coarse)').matches) {
            event.preventDefault(); if (canSend) event.currentTarget.form?.requestSubmit();
          }
        }} placeholder={working ? 'Follow up' : 'Message…'} aria-label={`Message ${agentName}`}/>
      <button type="button" className={`composer-icon-button composer-microphone${listening ? ' is-listening' : ''}`} disabled={controlsDisabled || !dictationAvailable}
        aria-label={dictationAvailable ? listening ? 'Stop dictation' : 'Dictate a message' : 'Dictation is unavailable on this device'} aria-pressed={listening}
        title={!dictationAvailable ? 'Dictation is unavailable on this device' : listening ? 'Stop dictation' : 'Dictate a message using your browser'} onClick={dictate}><MicrophoneIcon/></button>
      {working && hasContent && <button type="button" className="composer-icon-button composer-secondary-stop" disabled={disabled || sending}
        aria-label="Stop working" title="Stop working" onClick={() => {setError(null); void onStop().catch(failure => {if (mounted.current) setError(errorDetails(failure));});}}><StopIcon/></button>}
      {working && !hasContent
        ? <button type="button" className="composer-action" disabled={disabled || sending} aria-label="Stop working" title="Stop working"
            onClick={() => {setError(null); void onStop().catch(failure => {if (mounted.current) setError(errorDetails(failure));});}}><StopIcon/></button>
        : <button type="submit" className="composer-action" disabled={!canSend} aria-label="Send message" title="Send message"><SendIcon/></button>}
    </div>
    <ErrorNotice error={error}/>{errorContent}
  </form>;
}

function appendTranscript(before: string, phrase: string): string {
  return phrase ? before + (before && !/\s$/.test(before) ? ' ' : '') + phrase : before;
}

function formatFileSize(bytes: number): string {return bytes < 1024 * 1024 ? `${Math.max(1, Math.ceil(bytes / 1024))} KB` : `${(bytes / 1024 / 1024).toFixed(1)} MB`;}
function PlusIcon() {return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 4v16M4 12h16"/></svg>;}
function CloseIcon() {return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m7 7 10 10M17 7 7 17"/></svg>;}
function MicrophoneIcon() {return <svg viewBox="0 0 24 24" aria-hidden="true"><rect x="9" y="2.5" width="6" height="12" rx="3"/><path d="M5.5 11v1a6.5 6.5 0 0 0 13 0v-1M12 18.5v3M9 21.5h6"/></svg>;}
function StopIcon() {return <svg viewBox="0 0 24 24" aria-hidden="true"><rect x="6" y="6" width="12" height="12" rx="1.5" fill="currentColor" stroke="none"/></svg>;}
function SendIcon() {return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 19V5m-6 6 6-6 6 6"/></svg>;}
